import { useEffect, useRef, useState, useCallback } from 'react';

let _counter = 0;

// ── Colour palette by day group ───────────────────────────────────────────
const PALETTES = [
  { fill: '#dbeafe', stroke: '#3b82f6', text: '#1e3a8a' },  // blue  — early
  { fill: '#ede9fe', stroke: '#7c3aed', text: '#3b0764' },  // violet
  { fill: '#dcfce7', stroke: '#16a34a', text: '#14532d' },  // green
  { fill: '#fce7f3', stroke: '#db2777', text: '#831843' },  // pink
  { fill: '#fff7ed', stroke: '#ea580c', text: '#7c2d12' },  // orange
  { fill: '#f0fdf4', stroke: '#059669', text: '#064e3b' },  // emerald
];
const DECISION_PALETTE = { fill: '#fef9c3', stroke: '#ca8a04', text: '#713f12' };

function paletteForDay(dayStr) {
  const n = parseInt(dayStr) || 0;
  if (n <= 0) return PALETTES[0];
  const bucket = Math.floor((n - 1) / 3);
  return PALETTES[bucket % PALETTES.length];
}

function escapeXml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function wrapText(text, maxChars = 24) {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return ['Step'];
  const lines = [];
  let current = words[0];

  for (let i = 1; i < words.length; i += 1) {
    const candidate = `${current} ${words[i]}`;
    if (candidate.length <= maxChars) current = candidate;
    else {
      lines.push(current);
      current = words[i];
    }
  }
  lines.push(current);
  return lines.slice(0, 4);
}

function parseDayRange(dayStr) {
  if (!dayStr) return null;
  const match = String(dayStr).match(/(\d+)(?:\s*[–-]\s*(\d+))?/);
  if (!match) return null;
  const start = Number(match[1]);
  const end = Number(match[2] || match[1]);
  return { start, end };
}

function formatDuration(prevStep, nextStep) {
  const prev = parseDayRange(prevStep?.day);
  const next = parseDayRange(nextStep?.day);
  if (!prev || !next) return '';
  const low = Math.max(0, next.start - prev.end);
  const high = Math.max(low, next.end - prev.start);
  if (low === 0 && high === 0) return 'same day';
  if (low === high) return `${low} day${low === 1 ? '' : 's'}`;
  return `${low}-${high} days`;
}

function iconKindForStep(step) {
  const text = `${step.label} ${step.day}`.toLowerCase();
  if (step.isDecision) return 'decision';
  if (/(culture|cell|spleen|incubat|stimulat|plate|dish|pbmc)/.test(text)) return 'culture';
  if (/(inject|transfer|dose|treat|immuni|i\.p\.|s\.c\.|i\.v\.)/.test(text)) return 'syringe';
  if (/(mouse|mice|animal|eae|paralysis|clinical)/.test(text)) return 'animal';
  if (/(analy|measure|readout|score|collect|endpoint|observe)/.test(text)) return 'readout';
  return 'process';
}

function iconSvg(kind, x, y, size, color) {
  switch (kind) {
    case 'culture':
      return `
        <ellipse cx="${x + size / 2}" cy="${y + size * 0.28}" rx="${size * 0.34}" ry="${size * 0.14}" fill="none" stroke="${color}" stroke-width="2"/>
        <path d="M ${x + size * 0.16} ${y + size * 0.28} L ${x + size * 0.16} ${y + size * 0.62}" fill="none" stroke="${color}" stroke-width="2"/>
        <path d="M ${x + size * 0.84} ${y + size * 0.28} L ${x + size * 0.84} ${y + size * 0.62}" fill="none" stroke="${color}" stroke-width="2"/>
        <ellipse cx="${x + size / 2}" cy="${y + size * 0.62}" rx="${size * 0.34}" ry="${size * 0.14}" fill="rgba(255,99,71,0.18)" stroke="${color}" stroke-width="2"/>
        <circle cx="${x + size * 0.38}" cy="${y + size * 0.46}" r="${size * 0.035}" fill="${color}"/>
        <circle cx="${x + size * 0.52}" cy="${y + size * 0.5}" r="${size * 0.035}" fill="${color}"/>
        <circle cx="${x + size * 0.63}" cy="${y + size * 0.44}" r="${size * 0.035}" fill="${color}"/>
      `;
    case 'syringe':
      return `
        <path d="M ${x + size * 0.2} ${y + size * 0.68} L ${x + size * 0.8} ${y + size * 0.08}" fill="none" stroke="${color}" stroke-width="2.4" stroke-linecap="round"/>
        <rect x="${x + size * 0.32}" y="${y + size * 0.34}" width="${size * 0.26}" height="${size * 0.14}" rx="2" transform="rotate(-45 ${x + size * 0.45} ${y + size * 0.41})" fill="none" stroke="${color}" stroke-width="2"/>
        <path d="M ${x + size * 0.16} ${y + size * 0.72} L ${x + size * 0.28} ${y + size * 0.84}" fill="none" stroke="${color}" stroke-width="2"/>
        <path d="M ${x + size * 0.6} ${y + size * 0.14} L ${x + size * 0.72} ${y + size * 0.26}" fill="none" stroke="${color}" stroke-width="2"/>
        <path d="M ${x + size * 0.73} ${y + size * 0.15} L ${x + size * 0.88} ${y}" fill="none" stroke="${color}" stroke-width="1.8" stroke-linecap="round"/>
      `;
    case 'animal':
      return `
        <ellipse cx="${x + size * 0.48}" cy="${y + size * 0.52}" rx="${size * 0.24}" ry="${size * 0.18}" fill="none" stroke="${color}" stroke-width="2"/>
        <circle cx="${x + size * 0.3}" cy="${y + size * 0.38}" r="${size * 0.1}" fill="none" stroke="${color}" stroke-width="2"/>
        <circle cx="${x + size * 0.26}" cy="${y + size * 0.26}" r="${size * 0.05}" fill="none" stroke="${color}" stroke-width="2"/>
        <circle cx="${x + size * 0.35}" cy="${y + size * 0.24}" r="${size * 0.05}" fill="none" stroke="${color}" stroke-width="2"/>
        <circle cx="${x + size * 0.28}" cy="${y + size * 0.38}" r="${size * 0.012}" fill="${color}"/>
        <path d="M ${x + size * 0.68} ${y + size * 0.48} Q ${x + size * 0.92} ${y + size * 0.28} ${x + size * 0.86} ${y + size * 0.08}" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round"/>
      `;
    case 'readout':
      return `
        <rect x="${x + size * 0.24}" y="${y + size * 0.16}" width="${size * 0.5}" height="${size * 0.64}" rx="6" fill="none" stroke="${color}" stroke-width="2"/>
        <path d="M ${x + size * 0.36} ${y + size * 0.58} L ${x + size * 0.46} ${y + size * 0.46} L ${x + size * 0.55} ${y + size * 0.52} L ${x + size * 0.66} ${y + size * 0.34}" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M ${x + size * 0.38} ${y + size * 0.3} H ${x + size * 0.6}" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round"/>
      `;
    case 'decision':
      return `
        <path d="M ${x + size / 2} ${y + size * 0.12} L ${x + size * 0.85} ${y + size / 2} L ${x + size / 2} ${y + size * 0.88} L ${x + size * 0.15} ${y + size / 2} Z" fill="none" stroke="${color}" stroke-width="2"/>
        <path d="M ${x + size / 2} ${y + size * 0.3} V ${y + size * 0.56}" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round"/>
        <circle cx="${x + size / 2}" cy="${y + size * 0.7}" r="${size * 0.03}" fill="${color}"/>
      `;
    default:
      return `
        <rect x="${x + size * 0.22}" y="${y + size * 0.22}" width="${size * 0.56}" height="${size * 0.56}" rx="10" fill="none" stroke="${color}" stroke-width="2"/>
        <path d="M ${x + size * 0.34} ${y + size * 0.5} H ${x + size * 0.66}" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round"/>
        <path d="M ${x + size * 0.5} ${y + size * 0.34} V ${y + size * 0.66}" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round"/>
      `;
  }
}

function buildCard(step, x, y, cardW, cardH) {
  const palette = step.isDecision ? DECISION_PALETTE : paletteForDay(step.day);
  const title = wrapText(step.label || 'Step', 22);
  const subtitle = step.day ? `Day ${step.day}` : 'Protocol step';
  const iconKind = iconKindForStep(step);
  const iconSize = 54;
  const iconX = x + 22;
  const iconY = y + 24;
  const textX = x + cardW / 2;
  const textStartY = y + 98;
  const textLines = title
    .map((line, idx) => `<tspan x="${textX}" dy="${idx === 0 ? 0 : 18}">${escapeXml(line)}</tspan>`)
    .join('');

  return `
    <rect x="${x}" y="${y}" width="${cardW}" height="${cardH}" rx="22" fill="white" stroke="${palette.stroke}" stroke-width="2.2"/>
    <rect x="${x}" y="${y}" width="${cardW}" height="14" rx="22" fill="${palette.fill}" opacity="0.9"/>
    <circle cx="${x + cardW - 34}" cy="${y + 34}" r="18" fill="${palette.fill}" stroke="${palette.stroke}" stroke-width="1.5"/>
    ${iconSvg(iconKind, iconX, iconY, iconSize, palette.stroke)}
    <text x="${x + 22}" y="${y + cardH - 24}" fill="${palette.text}" font-size="12" font-weight="700">${escapeXml(subtitle)}</text>
    <text x="${textX}" y="${textStartY}" text-anchor="middle" fill="#1f2937" font-size="18" font-weight="700">${textLines}</text>
  `;
}

function buildCustomTimelineSvg(steps, direction = 'LR') {
  const safeSteps = steps.length
    ? steps
    : [{ id: 'A', day: '1', label: 'Add protocol steps', isDecision: false }];

  if (direction === 'TD') {
    const cardW = 460;
    const cardH = 120;
    const gap = 78;
    const marginY = 96;
    const width = 640;
    const height = marginY * 1.6 + safeSteps.length * cardH + Math.max(0, safeSteps.length - 1) * gap;

    let body = '';
    safeSteps.forEach((step, idx) => {
      const x = (width - cardW) / 2;
      const y = marginY + idx * (cardH + gap);
      body += buildCard(step, x, y, cardW, cardH);

      if (idx < safeSteps.length - 1) {
        const nextY = marginY + (idx + 1) * (cardH + gap);
        const lineX = width / 2;
        const label = formatDuration(step, safeSteps[idx + 1]);
        body += `
          <line x1="${lineX}" y1="${y + cardH}" x2="${lineX}" y2="${nextY - 26}" stroke="#374151" stroke-width="2.3"/>
          <polygon points="${lineX - 6},${nextY - 36} ${lineX + 6},${nextY - 36} ${lineX},${nextY - 24}" fill="#374151"/>
          ${label ? `<text x="${lineX + 14}" y="${y + cardH + gap / 2}" fill="#111827" font-size="15" font-weight="700">${escapeXml(label)}</text>` : ''}
        `;
      }
    });

    return {
      width,
      height,
      svg: `
        <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
          <rect x="18" y="18" width="${width - 36}" height="${height - 36}" rx="24" fill="#fcfcfd" stroke="#9ca3af" stroke-width="1.5"/>
          <text x="${width / 2}" y="58" text-anchor="middle" fill="#374151" font-size="22" font-weight="700">Experimental Protocol Timeline</text>
          ${body}
        </svg>
      `,
    };
  }

  const cardW = 250;
  const cardH = 156;
  const gap = 94;
  const marginX = 72;
  const marginY = 118;
  const width = Math.max(980, marginX * 2 + safeSteps.length * cardW + Math.max(0, safeSteps.length - 1) * gap);
  const height = 420;

  let body = '';
  safeSteps.forEach((step, idx) => {
    const x = marginX + idx * (cardW + gap);
    const y = marginY;
    body += buildCard(step, x, y, cardW, cardH);

    if (idx < safeSteps.length - 1) {
      const nextX = marginX + (idx + 1) * (cardW + gap);
      const startX = x + cardW;
      const endX = nextX - 16;
      const midX = (startX + endX) / 2;
      const label = formatDuration(step, safeSteps[idx + 1]);
      body += `
        <line x1="${startX + 10}" y1="${y + cardH / 2}" x2="${endX}" y2="${y + cardH / 2}" stroke="#374151" stroke-width="2.3"/>
        <polygon points="${endX - 12},${y + cardH / 2 - 7} ${endX},${y + cardH / 2} ${endX - 12},${y + cardH / 2 + 7}" fill="#374151"/>
        ${label ? `<text x="${midX}" y="${y + cardH / 2 - 16}" text-anchor="middle" fill="#111827" font-size="15" font-weight="700">${escapeXml(label)}</text>` : ''}
      `;
    }
  });

  return {
    width,
    height,
    svg: `
      <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
        <rect x="20" y="20" width="${width - 40}" height="${height - 40}" rx="24" fill="#fcfcfd" stroke="#9ca3af" stroke-width="1.5"/>
        <text x="${width / 2}" y="62" text-anchor="middle" fill="#374151" font-size="22" font-weight="700">Experimental Protocol Timeline</text>
        ${body}
      </svg>
    `,
  };
}

// ── Mermaid parser ────────────────────────────────────────────────────────
function parseMermaidSteps(code) {
  const steps = [];
  const seen = new Set();
  const rectRe = /^\s*([A-Za-z][A-Za-z0-9_]*)\[["']?([^\]"']+)["']?\]/gm;
  const diamondRe = /^\s*([A-Za-z][A-Za-z0-9_]*)\{+["']?([^}"']+)["']?\}+/gm;

  const extract = (re, isDecision) => {
    let m;
    while ((m = re.exec(code)) !== null) {
      const id = m[1];
      if (seen.has(id)) continue;
      seen.add(id);
      const raw = m[2].trim();
      const dayMatch = raw.match(/^Day\s*([\d–-]+):?\s*/i);
      steps.push({
        id,
        day: dayMatch ? dayMatch[1] : '',
        label: dayMatch ? raw.slice(dayMatch[0].length).trim() : raw,
        isDecision,
      });
    }
  };

  extract(rectRe, false);
  extract(diamondRe, true);

  const order = [];
  const orderRe = /^\s*([A-Za-z][A-Za-z0-9_]*)\s*(?:-->|$)/gm;
  let om;
  while ((om = orderRe.exec(code)) !== null) {
    if (!order.includes(om[1])) order.push(om[1]);
  }
  steps.sort((a, b) => {
    const ai = order.indexOf(a.id), bi = order.indexOf(b.id);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
  return steps;
}

// ── Mermaid builder ───────────────────────────────────────────────────────
function buildMermaid(steps, direction = 'TD') {
  if (!steps.length) return `flowchart ${direction}\n    A["No steps yet"]`;

  const ids = steps.map((_, i) =>
    i < 26 ? String.fromCharCode(65 + i) : `N${i}`
  );

  let nodeDefs = '';
  let classAssign = '';
  const classDefsSet = {};

  steps.forEach((step, i) => {
    const id = ids[i];
    const fullLabel = step.day
      ? `Day ${step.day}: ${step.label || 'Step'}`
      : (step.label || 'Step');

    const pal = step.isDecision ? DECISION_PALETTE : paletteForDay(step.day);
    const cls = step.isDecision ? 'decision' : `d${step.day || '0'}`;

    classDefsSet[cls] = pal;

    if (step.isDecision) {
      nodeDefs += `    ${id}{"${fullLabel}"}\n`;
    } else {
      nodeDefs += `    ${id}["${fullLabel}"]\n`;
    }
    classAssign += `    class ${id} ${cls}\n`;
  });

  let classDefs = '';
  for (const [cls, pal] of Object.entries(classDefsSet)) {
    classDefs += `    classDef ${cls} fill:${pal.fill},stroke:${pal.stroke},color:${pal.text},stroke-width:2px,font-weight:500,rx:10\n`;
  }

  const connections = '    ' + ids.join(' --> ') + '\n';

  return `flowchart ${direction}\n${classDefs}${nodeDefs}${connections}${classAssign}`;
}

// ── Step Editor ───────────────────────────────────────────────────────────
function StepEditor({ steps, onChange }) {
  const update = (i, field, value) =>
    onChange(steps.map((s, j) => (j === i ? { ...s, [field]: value } : s)));

  const addStep = () => {
    const lastDay = parseInt(steps[steps.length - 1]?.day) || 0;
    onChange([...steps, { id: `X${Date.now()}`, day: String(lastDay + 1), label: '', isDecision: false }]);
  };

  const remove = (i) => onChange(steps.filter((_, j) => j !== i));

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    const next = [...steps];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-gray-100 bg-white">
        <p className="text-sm font-semibold text-gray-800">Edit Steps</p>
        <p className="text-xs text-gray-400 mt-0.5">Chart updates as you type</p>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5">
        {steps.map((step, i) => {
          const pal = step.isDecision ? DECISION_PALETTE : paletteForDay(step.day);
          return (
            <div
              key={step.id}
              className="rounded-xl border border-gray-100 shadow-sm p-3 bg-white border-l-4"
              style={{ borderLeftColor: pal.stroke }}
            >
              <div className="flex items-center gap-2 mb-2">
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                  style={{ backgroundColor: pal.stroke }}
                >
                  {i + 1}
                </div>
                <span className="text-xs font-medium text-gray-500">Day</span>
                <input
                  type="text"
                  value={step.day}
                  onChange={(e) => update(i, 'day', e.target.value)}
                  placeholder="1"
                  className="w-12 border border-gray-200 rounded-lg px-2 py-1 text-sm text-center bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300"
                />
                <div className="ml-auto">
                  {step.isDecision ? (
                    <button
                      type="button"
                      onClick={() => update(i, 'isDecision', false)}
                      className="flex items-center gap-1 bg-amber-50 border border-amber-300 text-amber-700 rounded-full px-2 py-0.5 text-xs cursor-pointer select-none"
                    >
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M5 1L9 5L5 9L1 5Z" fill="#d97706" stroke="#b45309" strokeWidth="0.5" />
                      </svg>
                      Decision
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => update(i, 'isDecision', true)}
                      className="flex items-center gap-1 border border-gray-200 text-gray-400 rounded-full px-2 py-0.5 text-xs cursor-pointer select-none hover:border-amber-300 hover:text-amber-600 hover:bg-amber-50"
                    >
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M5 1L9 5L5 9L1 5Z" stroke="#d1d5db" strokeWidth="1" />
                      </svg>
                      Decision
                    </button>
                  )}
                </div>
              </div>

              <textarea
                value={step.label}
                onChange={(e) => update(i, 'label', e.target.value)}
                rows={2}
                placeholder="Describe this step..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300"
              />

              <div className="flex items-center gap-1 mt-2 justify-end">
                <button onClick={() => move(i, -1)} disabled={i === 0}
                  className="px-2 py-0.5 text-xs border border-gray-200 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-30">↑</button>
                <button onClick={() => move(i, 1)} disabled={i === steps.length - 1}
                  className="px-2 py-0.5 text-xs border border-gray-200 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-30">↓</button>
                <button onClick={() => remove(i)}
                  className="px-2 py-0.5 text-xs text-red-500 border border-red-200 rounded-lg bg-white hover:bg-red-50 ml-1">✕ Remove</button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="px-3 py-3 border-t border-gray-100">
        <button
          onClick={addStep}
          className="w-full py-2.5 rounded-xl border-2 border-dashed border-indigo-200 text-sm text-indigo-500 font-medium hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
        >
          + Add Step
        </button>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────
export default function ProtocolFlowchart({ initialCode, onClose }) {
  const [steps, setSteps] = useState(() => parseMermaidSteps(initialCode || ''));
  const [code, setCode] = useState(initialCode || '');
  const [editMode, setEditMode] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [error, setError] = useState('');
  const [direction, setDirection] = useState('LR');
  const [legendOpen, setLegendOpen] = useState(false);
  const diagramRef = useRef(null);
  const idRef = useRef(`timeline-${++_counter}`);

  // Store natural SVG dimensions so zoom can scale width/height directly
  const naturalSizeRef = useRef({ w: 0, h: 0 });

  const applyZoom = useCallback((z) => {
    const svgEl = diagramRef.current?.querySelector('svg');
    if (!svgEl || !naturalSizeRef.current.w) return;
    svgEl.style.width = `${naturalSizeRef.current.w * z}px`;
    svgEl.style.height = `${naturalSizeRef.current.h * z}px`;
  }, []);

  // Re-apply zoom whenever zoom state changes (without re-rendering mermaid)
  useEffect(() => { applyZoom(zoom); }, [zoom, applyZoom]);

  const renderDiagram = useCallback(async (source, dir) => {
    if (!diagramRef.current || !source.trim()) return;
    setError('');

    try {
      const parsedSteps = parseMermaidSteps(source);
      const uid = idRef.current + '-' + Date.now();
      const { svg, width, height } = buildCustomTimelineSvg(parsedSteps, dir);
      diagramRef.current.innerHTML = svg.replace('<svg ', `<svg id="${uid}" `);
      const svgEl = diagramRef.current.querySelector('svg');
      if (svgEl) {
        svgEl.style.background = 'transparent';
        svgEl.style.display = 'block';
        naturalSizeRef.current = { w: width, h: height };
        svgEl.removeAttribute('width');
        svgEl.removeAttribute('height');
        applyZoom(zoom);
      }
    } catch {
      setError('Could not render diagram — try adjusting the steps.');
    }
  }, [applyZoom]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!editMode) return;
    const newCode = buildMermaid(steps, direction);
    setCode(newCode);
    renderDiagram(newCode, direction);
  }, [steps, editMode, direction, renderDiagram]);

  useEffect(() => {
    renderDiagram(code, direction);
  }, [code, direction, renderDiagram]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = () => {
    const svgEl = diagramRef.current?.querySelector('svg');
    if (!svgEl) return;
    const blob = new Blob([svgEl.outerHTML], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = 'protocol-flowchart.svg'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleToggleEdit = () => {
    if (!editMode) setSteps(parseMermaidSteps(code));
    setEditMode((e) => !e);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className={`bg-white rounded-2xl shadow-2xl ring-1 ring-black/10 w-full flex flex-col overflow-hidden transition-all duration-300 ${direction === 'LR' ? 'max-w-[96vw] h-[92vh]' : 'max-w-5xl max-h-[92vh]'}`}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 bg-[#fafafa]" style={{ minHeight: '56px' }}>
          <div className="flex items-center gap-2">
            {/* Flowchart icon */}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <rect x="5" y="1" width="6" height="4" rx="1" fill="#4f46e5" />
              <rect x="1" y="10" width="5" height="4" rx="1" fill="#4f46e5" opacity="0.7" />
              <rect x="10" y="10" width="5" height="4" rx="1" fill="#4f46e5" opacity="0.7" />
              <line x1="8" y1="5" x2="8" y2="8" stroke="#94a3b8" strokeWidth="1.5" />
              <line x1="8" y1="8" x2="3.5" y2="10" stroke="#94a3b8" strokeWidth="1.5" />
              <line x1="8" y1="8" x2="12.5" y2="10" stroke="#94a3b8" strokeWidth="1.5" />
            </svg>
            <h2
              className="font-semibold text-[#171717]"
              style={{ fontSize: '15px', letterSpacing: '-0.3px' }}
            >
              Protocol Timeline
            </h2>
          </div>

          <div className="flex items-center gap-2">
            {/* Zoom controls */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg px-2 py-1">
              <button
                onClick={() => setZoom((z) => Math.max(0.4, +(z - 0.15).toFixed(2)))}
                className="text-gray-600 hover:text-gray-900 w-5 h-5 flex items-center justify-center"
                aria-label="Zoom out"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <line x1="2" y1="6" x2="10" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
              <span className="text-xs text-gray-500 w-10 text-center">{Math.round(zoom * 100)}%</span>
              <button
                onClick={() => setZoom((z) => Math.min(2.5, +(z + 0.15).toFixed(2)))}
                className="text-gray-600 hover:text-gray-900 w-5 h-5 flex items-center justify-center"
                aria-label="Zoom in"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <line x1="6" y1="2" x2="6" y2="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <line x1="2" y1="6" x2="10" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <button
              onClick={() => setZoom(1)}
              className="text-[11px] text-gray-500 hover:text-gray-700 px-2 py-1 rounded-lg hover:bg-gray-100"
            >
              100%
            </button>

            <div className="w-px h-5 bg-gray-200 mx-1" />

            {/* Direction segmented control */}
            <div className="flex items-center rounded-lg overflow-hidden border border-gray-200 text-xs font-medium">
              <button
                onClick={() => { setDirection('TD'); setZoom(1); }}
                className={`px-3 py-1.5 transition-colors ${
                  direction === 'TD'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                }`}
              >
                Stacked
              </button>
              <button
                onClick={() => { setDirection('LR'); setZoom(0.75); }}
                className={`px-3 py-1.5 transition-colors border-l border-gray-200 ${
                  direction === 'LR'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                }`}
              >
                Figure
              </button>
            </div>

            <div className="w-px h-5 bg-gray-200 mx-1" />

            {/* Edit Steps button */}
            <button
              onClick={handleToggleEdit}
              className={`px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                editMode
                  ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-200'
                  : 'bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100'
              }`}
            >
              {editMode ? '✓ Done' : 'Edit Steps'}
            </button>

            {/* Export SVG button */}
            <button
              onClick={handleDownload}
              className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
                <path d="M5.5 1v6M2.5 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                <line x1="1" y1="9.5" x2="10" y2="9.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
              Export SVG
            </button>

            <button
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors text-lg leading-none"
            >
              ×
            </button>
          </div>
        </div>

        {/* Legend (collapsible) */}
        <div className="border-b border-gray-100 bg-gray-50">
          <button
            onClick={() => setLegendOpen((v) => !v)}
            className="flex items-center gap-1.5 px-5 py-2 text-xs text-gray-500 hover:text-gray-700 font-medium select-none"
          >
            <span>{legendOpen ? '▾' : '▸'}</span>
            Legend
          </button>
          {legendOpen && (
            <div className="flex flex-wrap items-center gap-2 px-5 pb-2.5 text-xs text-gray-500">
              {PALETTES.slice(0, 4).map((p, i) => (
                <span
                  key={i}
                  className="inline-flex items-center rounded-full px-2 py-0.5 font-medium border"
                  style={{ background: p.fill, borderColor: p.stroke, color: p.text }}
                >
                  Day {i * 3 + 1}–{i * 3 + 3}
                </span>
              ))}
              <span
                className="inline-flex items-center rounded-full px-2 py-0.5 font-medium border"
                style={{ background: DECISION_PALETTE.fill, borderColor: DECISION_PALETTE.stroke, color: DECISION_PALETTE.text }}
              >
                Decision
              </span>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Diagram canvas — overflow-auto gives scroll in both axes */}
          <div
            className="flex-1 overflow-auto"
            style={{
              background: 'radial-gradient(circle, #cbd5e1 1px, transparent 1px)',
              backgroundSize: '20px 20px',
              backgroundColor: '#f8fafc',
            }}
          >
            {error && (
              <div className="m-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700">
                {error}
              </div>
            )}
            <div style={{ padding: '32px 24px', display: 'inline-block', minWidth: '100%' }}>
              <div
                ref={diagramRef}
                style={{ filter: 'drop-shadow(0 4px 24px rgba(99,102,241,0.10))' }}
              />
            </div>
          </div>

          {/* Step editor panel */}
          {editMode && (
            <div className="w-72 flex-shrink-0 border-l border-gray-100 flex flex-col bg-white">
              <StepEditor steps={steps} onChange={setSteps} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
